import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useLanguage } from '@/contexts/LanguageContext';
import { getMeetingTranslations } from '@/constants/meeting.translations';
import { MeetingTranscription, MeetingSummary } from '@/types/meeting.types';
import { 
  FileText, 
  Search, 
  Download, 
  Clock, 
  Users, 
  CheckCircle, 
  ListTodo, 
  AlertTriangle,
  MessageSquare,
  Sparkles
} from 'lucide-react';

interface TranscriptionViewerProps {
  transcription: MeetingTranscription;
  onGenerateSummary?: () => void;
  isGeneratingSummary?: boolean;
}

const TranscriptionViewer: React.FC<TranscriptionViewerProps> = ({
  transcription,
  onGenerateSummary,
  isGeneratingSummary = false,
}) => {
  const { language } = useLanguage();
  const t = getMeetingTranslations(language);
  const [searchQuery, setSearchQuery] = useState('');

  const formatDuration = (seconds?: number): string => {
    if (!seconds) return '—';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatTimestamp = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const highlightText = (text: string, query: string): React.ReactNode => {
    if (!query.trim()) return text;
    
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, i) => 
      part.toLowerCase() === query.toLowerCase() ? (
        <mark key={i} className="bg-yellow-200 dark:bg-yellow-800 px-0.5 rounded">
          {part}
        </mark>
      ) : part
    );
  };

  const filteredSegments = transcription.transcriptionSegments?.filter(
    seg => seg.text.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const summary = transcription.summary as MeetingSummary | undefined;

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'fraud_attempt':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case 'contact_exchange':
        return <AlertTriangle className="h-4 w-4 text-orange-500" />;
      case 'off_platform':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      default:
        return <AlertTriangle className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Status and Meta */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {t.transcription}
            </CardTitle>
            <Badge 
              variant={
                transcription.status === 'completed' ? 'default' :
                transcription.status === 'processing' ? 'secondary' :
                transcription.status === 'failed' ? 'destructive' : 'outline'
              }
            >
              {t[transcription.status]}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              {formatDuration(transcription.durationSeconds)}
            </div>
            <div className="flex items-center gap-1">
              <MessageSquare className="h-4 w-4" />
              {transcription.wordCount?.toLocaleString() || 0} {language === 'fr' ? 'mots' : 'words'}
            </div>
            <div>
              {language === 'fr' ? 'Langue' : 'Language'}: {transcription.language?.toUpperCase()}
            </div>
          </div>

          {transcription.status === 'completed' && !summary && onGenerateSummary && (
            <Button 
              onClick={onGenerateSummary} 
              disabled={isGeneratingSummary}
              className="mt-4 bg-[#CEAB65] hover:bg-[#CEAB65]/90"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              {isGeneratingSummary ? 'Generating...' : t.generateSummary}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Summary Section */}
      {summary && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-[#CEAB65]" />
              {t.summary}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Participants */}
            {summary.participants?.length > 0 && (
              <div>
                <h4 className="font-medium flex items-center gap-2 mb-2">
                  <Users className="h-4 w-4" />
                  {t.participants}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {summary.participants.map((p, i) => (
                    <Badge key={i} variant="secondary">{p}</Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Decisions */}
            {summary.decisions?.length > 0 && (
              <div>
                <h4 className="font-medium flex items-center gap-2 mb-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  {t.decisions}
                </h4>
                <ul className="space-y-1">
                  {summary.decisions.map((d, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <span className="text-green-500">✓</span>
                      {d}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Action Items */}
            {summary.actionItems?.length > 0 && (
              <div>
                <h4 className="font-medium flex items-center gap-2 mb-2">
                  <ListTodo className="h-4 w-4 text-blue-500" />
                  {t.actionItems}
                </h4>
                <ul className="space-y-2">
                  {summary.actionItems.map((item, i) => (
                    <li key={i} className="text-sm p-2 bg-muted rounded-md">
                      <div className="font-medium">{item.task}</div>
                      <div className="text-muted-foreground text-xs mt-1">
                        {language === 'fr' ? 'Responsable' : 'Assignee'}: {item.assignee}
                        {item.deadline && ` • ${language === 'fr' ? 'Échéance' : 'Deadline'}: ${item.deadline}`}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Key Topics */}
            {summary.keyTopics?.length > 0 && (
              <div>
                <h4 className="font-medium mb-2">{t.keyTopics}</h4>
                <div className="flex flex-wrap gap-2">
                  {summary.keyTopics.map((topic, i) => (
                    <Badge key={i} variant="outline">{topic}</Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Alerts */}
            {summary.alerts && summary.alerts.length > 0 && (
              <div className="border border-red-200 dark:border-red-800 rounded-lg p-4 bg-red-50 dark:bg-red-950">
                <h4 className="font-medium flex items-center gap-2 mb-2 text-red-700 dark:text-red-300">
                  <AlertTriangle className="h-4 w-4" />
                  {t.alerts}
                </h4>
                <ul className="space-y-2">
                  {summary.alerts.map((alert, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      {getAlertIcon(alert.type)}
                      <div>
                        <span className="font-medium">
                          {t[alert.type as keyof typeof t] || alert.type}:
                        </span>{' '}
                        {alert.description}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Full Transcription */}
      {transcription.transcriptionText && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">{t.transcription}</CardTitle>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                {t.downloadTranscription}
              </Button>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t.searchTranscription}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px] pr-4">
              {transcription.transcriptionSegments && transcription.transcriptionSegments.length > 0 ? (
                <div className="space-y-3">
                  {(filteredSegments || transcription.transcriptionSegments).map((segment, i) => (
                    <div key={i} className="flex gap-3">
                      <span className="text-xs text-muted-foreground font-mono whitespace-nowrap pt-0.5">
                        [{formatTimestamp(segment.start)}]
                      </span>
                      <p className="text-sm">
                        {highlightText(segment.text, searchQuery)}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm whitespace-pre-wrap">
                  {highlightText(transcription.transcriptionText, searchQuery)}
                </p>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TranscriptionViewer;
